if (!customElements.get('section-home-bestsellers')) {
  customElements.define(
    'section-home-bestsellers',
    class HomeBestsellers extends HTMLElement {
      static DESKTOP_MQ = '(min-width: 750px)';
      static LARGE_MQ = '(min-width: 1200px)';
      static MOBILE_PAGE_SIZE = 4;
      static MOBILE_ROW_SIZE = 2;

      constructor() {
        super();
        this._initialized = false;
        this._activeSwiper = null;
        this._refreshTimer = null;
        this._onMqChange = this.syncLayout.bind(this);
      }

      connectedCallback() {
        const start = () => this.observeElement(this, this.handleInViewport.bind(this));
        if (document.readyState === 'loading') {
          window.addEventListener('DOMContentLoaded', start, { once: true });
        } else {
          start();
        }
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this._mq) this._mq.removeEventListener('change', this._onMqChange);
        if (this._largeMq) this._largeMq.removeEventListener('change', this._onMqChange);
        if (this._refreshTimer) window.clearTimeout(this._refreshTimer);
        this.destroySwiper();
      }

      handleInViewport() {
        if (this._initialized) return;
        this._initialized = true;
        this.init();
      }

      observeElement(element, callback) {
        this.observer = new IntersectionObserver(
          (entries, observerInstance) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                callback();
                observerInstance.unobserve(entry.target);
              }
            });
          },
          { root: null, threshold: 0.2 }
        );

        if (this.isElementInViewport(element)) {
          callback();
          return;
        }
        this.observer.observe(element);
      }

      isElementInViewport(el) {
        const rect = el.getBoundingClientRect();
        return (
          rect.top >= -el.offsetHeight / 2 &&
          rect.bottom <=
            (window.innerHeight || document.documentElement.clientHeight) + el.offsetHeight / 2
        );
      }

      isDesktop() {
        return window.matchMedia(HomeBestsellers.DESKTOP_MQ).matches;
      }

      isLarge() {
        return window.matchMedia(HomeBestsellers.LARGE_MQ).matches;
      }

      isMobileRow() {
        return (this.dataset.mobileLayout || 'grid') === 'row';
      }

      isLoopEnabled() {
        return this.dataset.loop === 'true';
      }

      getSlidesPerView(name, fallback) {
        const value = parseFloat(this.dataset[name]);
        return Number.isFinite(value) && value > 0 ? value : fallback;
      }

      getDesktopPageSize() {
        const slides = this.isLarge()
          ? this.getSlidesPerView('pcSlides', 4.3)
          : this.getSlidesPerView('padSlides', 3.3);
        return Math.max(1, Math.floor(slides));
      }

      getDesktopGap() {
        return parseFloat(getComputedStyle(this).columnGap) || 0;
      }

      init() {
        this.tabButtons = Array.from(this.querySelectorAll('[data-bestsellers-tab]'));
        this.tabList = this.querySelector('[data-bestsellers-tabs]');
        this.panels = Array.from(this.querySelectorAll('[data-tab-panel]'));
        this.prevBtn = this.querySelector('[data-bestsellers-prev]');
        this.nextBtn = this.querySelector('[data-bestsellers-next]');
        this._mq = window.matchMedia(HomeBestsellers.DESKTOP_MQ);
        this._largeMq = window.matchMedia(HomeBestsellers.LARGE_MQ);

        this.tabButtons.forEach((btn) => {
          const isActive = btn.classList.contains('is-active');
          btn.tabIndex = isActive ? 0 : -1;
          btn.addEventListener('click', () => this.activateTab(btn.dataset.bestsellersTab));
        });
        if (this.tabList) {
          this.tabList.addEventListener('keydown', (event) => this.onTabKeydown(event));
          this.tabList.addEventListener('focusout', (event) => this.onTabListFocusOut(event));
        }

        if (this.prevBtn) {
          this.prevBtn.addEventListener('click', () => this._activeSwiper?.slidePrev());
        }
        if (this.nextBtn) {
          this.nextBtn.addEventListener('click', () => this._activeSwiper?.slideNext());
        }

        this._mq.addEventListener('change', this._onMqChange);
        this._largeMq.addEventListener('change', this._onMqChange);
        this.whenSwiperReady(() => this.syncLayout());
      }

      whenSwiperReady(callback) {
        if (typeof Swiper !== 'undefined') {
          callback();
          return;
        }

        let attempts = 0;
        const timer = window.setInterval(() => {
          attempts += 1;
          if (typeof Swiper !== 'undefined') {
            window.clearInterval(timer);
            callback();
            return;
          }
          if (attempts >= 100) window.clearInterval(timer);
        }, 50);
      }

      onTabKeydown(event) {
        if (event.altKey || event.ctrlKey || event.metaKey) return;

        const current = event.target.closest('[data-bestsellers-tab]');
        if (!current || !this.tabButtons.includes(current) || !this.tabButtons.length) return;

        const rtl = getComputedStyle(this.tabList || current).direction === 'rtl';
        const prevKey = rtl ? 'ArrowRight' : 'ArrowLeft';
        const nextKey = rtl ? 'ArrowLeft' : 'ArrowRight';
        const index = this.tabButtons.indexOf(current);
        let nextIndex = -1;

        switch (event.key) {
          case nextKey:
            nextIndex = (index + 1) % this.tabButtons.length;
            break;
          case prevKey:
            nextIndex = (index - 1 + this.tabButtons.length) % this.tabButtons.length;
            break;
          case 'Home':
            nextIndex = 0;
            break;
          case 'End':
            nextIndex = this.tabButtons.length - 1;
            break;
          default:
            return;
        }

        event.preventDefault();
        if (event.repeat) return;

        const next = this.tabButtons[nextIndex];
        if (next) this.focusTab(next);
      }

      onTabListFocusOut(event) {
        if (this.tabList.contains(event.relatedTarget)) return;
        this.syncRovingTabindex();
      }

      syncRovingTabindex(focusedTab) {
        const selected = this.tabButtons.find((btn) => btn.getAttribute('aria-selected') === 'true');
        const tab = focusedTab || selected || this.tabButtons[0];
        this.tabButtons.forEach((btn) => {
          btn.tabIndex = btn === tab ? 0 : -1;
        });
      }

      focusTab(tab) {
        if (!tab) return;
        this.syncRovingTabindex(tab);
        tab.focus({ preventScroll: true });
        this.scrollTabIntoView(tab);
      }

      activateTab(tabId) {
        if (!tabId) return;

        const nextTab = this.tabButtons.find((btn) => btn.dataset.bestsellersTab === tabId);
        if (!nextTab) return;

        const alreadyActive = nextTab.classList.contains('is-active');
        if (!alreadyActive) {
          this.tabButtons.forEach((btn) => {
            const isActive = btn === nextTab;
            btn.classList.toggle('is-active', isActive);
            btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
          });

          this.panels.forEach((panel) => {
            const isActive = panel.dataset.tabPanel === tabId;
            panel.hidden = !isActive;
            if ('inert' in panel) panel.inert = !isActive;
          });

          this.scrollTabIntoView(nextTab);
          this.syncLayout();
        }

        this.retainTabFocus(nextTab);
      }

      retainTabFocus(tab) {
        if (!tab) return;
        this._pendingTabFocus = tab;
        this.syncRovingTabindex(tab);
        this.flushPendingTabFocus();
        requestAnimationFrame(() => {
          this.flushPendingTabFocus();
          requestAnimationFrame(() => {
            this.flushPendingTabFocus();
            this._pendingTabFocus = null;
          });
        });
      }

      flushPendingTabFocus() {
        const tab = this._pendingTabFocus;
        if (!tab?.isConnected) return;
        if (document.activeElement !== tab) {
          tab.focus({ preventScroll: true });
        }
      }

      scrollTabIntoView(tab) {
        const tabList = this.tabList || this.querySelector('[data-bestsellers-tabs]');
        if (!tabList || !tab || tabList.scrollWidth <= tabList.clientWidth) return;

        const btnRect = tab.getBoundingClientRect();
        const listRect = tabList.getBoundingClientRect();
        const target =
          tabList.scrollLeft + btnRect.left - listRect.left - (listRect.width - btnRect.width) / 2;
        const max = tabList.scrollWidth - tabList.clientWidth;
        const next = Math.max(0, Math.min(target, max));

        if (typeof tabList.scrollTo === 'function') {
          tabList.scrollTo({ left: next, behavior: 'smooth' });
        } else {
          tabList.scrollLeft = next;
        }
      }

      getActivePanel() {
        return this.panels.find((panel) => !panel.hidden) || this.panels[0];
      }

      resetColorSwatches(root) {
        if (!root) return;
        root.querySelectorAll('color-change-component').forEach((comp) => {
          comp.pageStart = 0;
          comp.pageHistory = [0];
          if (typeof comp.resetOverflowState === 'function') {
            comp.resetOverflowState();
            return;
          }
          const fieldset = comp.querySelector('fieldset');
          if (fieldset) fieldset.style.width = '';
          comp.querySelectorAll('.s-item').forEach((item) => item.classList.remove('d-none', 'd-block'));
          const moreBtn = comp.querySelector('.over-swatch-btn');
          if (moreBtn) {
            moreBtn.classList.add('hidden');
            moreBtn.classList.remove('d-none');
            if (moreBtn.parentElement) moreBtn.parentElement.classList.remove('d-none');
          }
          const back = comp.querySelector('.backtoleft');
          if (back) back.classList.add('d-none');
        });
      }

      uniqueCloneSwatchIds(root) {
        if (!root) return;
        root.querySelectorAll('.swiper-slide-duplicate color-change-component').forEach((comp, index) => {
          comp.querySelectorAll('[id]').forEach((el) => {
            const oldId = el.id;
            if (!oldId) return;
            const newId = `${oldId}-clone-${index}`;
            el.id = newId;
            const label = comp.querySelector(`label[for="${oldId}"]`);
            if (label) label.setAttribute('for', newId);
          });
          comp.querySelectorAll('input[name]').forEach((input) => {
            input.name = `${input.name}-clone-${index}`;
          });
        });
      }

      refreshColorSwatches(root) {
        if (!root) return;
        this.resetColorSwatches(root);
        this.uniqueCloneSwatchIds(root);
        root.querySelectorAll('color-change-component').forEach((comp) => {
          const fieldset = comp.querySelector('fieldset');
          const moreBtn = comp.querySelector('.over-swatch-btn');
          const countNum = moreBtn?.querySelector('.more-swatch');
          const items = comp.querySelectorAll('.s-item');
          if (!fieldset || typeof comp.init !== 'function' || !fieldset.clientWidth) return;
          comp.init(fieldset, items, moreBtn, countNum);
        });
      }

      scheduleColorSwatchRefresh(root) {
        this.resetColorSwatches(root);
        if (this._refreshTimer) window.clearTimeout(this._refreshTimer);
        this._refreshTimer = window.setTimeout(() => this.refreshColorSwatches(root), 250);
      }

      applyDesktopGap(swiper) {
        if (!swiper || !this.isDesktop()) return;
        const gap = this.getDesktopGap();
        if ((swiper.params.spaceBetween || 0) === gap) return;
        swiper.params.spaceBetween = gap;
        swiper.update();
      }

      lockSwiperScroll(el) {
        if (!el || el.dataset.scrollLocked) return;
        el.dataset.scrollLocked = 'true';
        const reset = () => this.resetSwiperScroll(el);
        el.addEventListener('scroll', reset, { passive: true });
        el.addEventListener('focusin', () => {
          reset();
          requestAnimationFrame(reset);
        });
      }

      resetSwiperScroll(el) {
        let node = el || this.querySelector('[data-bestsellers-swiper]');
        const stop = this.parentElement;
        while (node && node !== stop) {
          if (node.scrollLeft) node.scrollLeft = 0;
          node = node.parentElement;
        }
      }

      ungroupMobileSlides(wrapper) {
        if (!wrapper) return;
        const pages = Array.from(wrapper.querySelectorAll(':scope > .home-bestsellers__page'));
        if (!pages.length) return;

        pages.forEach((page) => {
          Array.from(page.children).forEach((slide) => {
            slide.classList.add('swiper-slide');
            wrapper.insertBefore(slide, page);
          });
          page.remove();
        });
      }

      ungroupAllMobileSlides() {
        this.querySelectorAll('[data-bestsellers-swiper] .swiper-wrapper').forEach((wrapper) => {
          this.ungroupMobileSlides(wrapper);
        });
      }

      groupMobileSlides(wrapper) {
        if (!wrapper || wrapper.querySelector(':scope > .home-bestsellers__page')) return;

        const slides = Array.from(wrapper.querySelectorAll(':scope > .home-bestsellers__slide'));
        const pageSize = HomeBestsellers.MOBILE_PAGE_SIZE;
        if (slides.length <= pageSize) return;

        for (let i = 0; i < slides.length; i += pageSize) {
          const page = document.createElement('div');
          page.className = 'swiper-slide home-bestsellers__page';
          slides.slice(i, i + pageSize).forEach((slide) => {
            slide.classList.remove('swiper-slide');
            page.appendChild(slide);
          });
          wrapper.appendChild(page);
        }
      }

      getSharedSwiperParams() {
        return {
          watchOverflow: true,
          // Lets a11y treat on-screen cards as visible, so focusing a swatch
          // does not slideTo that card to the left.
          watchSlidesProgress: true,
          observer: false,
          observeParents: false,
          resizeObserver: false,
          slideToClickedSlide: false,
          noSwiping: true,
          noSwipingSelector: 'color-change-component',
        };
      }

      getDesktopSwiperOptions() {
        const padSlides = this.getSlidesPerView('padSlides', 3.3);
        const pcSlides = this.getSlidesPerView('pcSlides', 4.3);
        return {
          slidesPerView: padSlides,
          slidesPerGroup: 1,
          spaceBetween: this.getDesktopGap(),
          speed: 500,
          loop: this.isLoopEnabled(),
          ...this.getSharedSwiperParams(),
          breakpoints: {
            1200: {
              slidesPerView: pcSlides,
            },
          },
          on: {
            resize: (swiper) => this.applyDesktopGap(swiper),
            slideChange: () => this.updateArrowState(),
            reachBeginning: () => this.updateArrowState(),
            reachEnd: () => this.updateArrowState(),
          },
        };
      }

      getMobileSwiperOptions(el) {
        const paginationEl = el.querySelector('[data-bestsellers-pagination]');
        return {
          slidesPerView: 1,
          slidesPerGroup: 1,
          spaceBetween: 15,
          speed: 500,
          loop: this.isLoopEnabled(),
          ...this.getSharedSwiperParams(),
          pagination: paginationEl
            ? {
                el: paginationEl,
                clickable: true,
              }
            : undefined,
          on: {
            slideChange: () => this.updateArrowState(),
            reachBeginning: () => this.updateArrowState(),
            reachEnd: () => this.updateArrowState(),
          },
        };
      }

      getMobileRowSwiperOptions(el) {
        const paginationEl = el.querySelector('[data-bestsellers-pagination]');
        const pageSize = HomeBestsellers.MOBILE_ROW_SIZE;
        return {
          slidesPerView: pageSize,
          slidesPerGroup: pageSize,
          spaceBetween: 12,
          speed: 500,
          loop: this.isLoopEnabled(),
          ...this.getSharedSwiperParams(),
          pagination: paginationEl
            ? {
                el: paginationEl,
                clickable: true,
              }
            : undefined,
          on: {
            slideChange: () => this.updateArrowState(),
            reachBeginning: () => this.updateArrowState(),
            reachEnd: () => this.updateArrowState(),
          },
        };
      }

      syncLayout() {
        this.destroySwiper();
        this.ungroupAllMobileSlides();

        const panel = this.getActivePanel();
        const el = panel?.querySelector('[data-bestsellers-swiper]');
        if (!el) {
          this.classList.remove('has-arrows', 'is-static');
          return;
        }

        const wrapper = el.querySelector('.swiper-wrapper');
        const productCount = wrapper
          ? wrapper.querySelectorAll(':scope > .home-bestsellers__slide').length
          : 0;
        const isDesktop = this.isDesktop();
        const isMobileRow = !isDesktop && this.isMobileRow();
        const pageSize = isDesktop
          ? this.getDesktopPageSize()
          : isMobileRow
            ? HomeBestsellers.MOBILE_ROW_SIZE
            : HomeBestsellers.MOBILE_PAGE_SIZE;
        const canSlide = productCount > pageSize;

        this.classList.toggle('has-arrows', canSlide);
        this.classList.toggle('is-static', !canSlide);

        if (!canSlide || typeof Swiper === 'undefined') {
          this.lockSwiperScroll(el);
          this.scheduleColorSwatchRefresh(panel);
          return;
        }

        if (!isDesktop && !isMobileRow) this.groupMobileSlides(wrapper);

        requestAnimationFrame(() => {
          if (el.swiper) return;
          const options = isDesktop
            ? this.getDesktopSwiperOptions()
            : isMobileRow
              ? this.getMobileRowSwiperOptions(el)
              : this.getMobileSwiperOptions(el);
          this._activeSwiper = new Swiper(el, options);
          this.lockSwiperScroll(el);
          this.updateArrowState();
          this.scheduleColorSwatchRefresh(panel);
          this.flushPendingTabFocus();
        });
      }

      updateArrowState() {
        const swiper = this._activeSwiper;
        if (!this.prevBtn || !this.nextBtn) return;

        if (!swiper || swiper.params.loop) {
          this.prevBtn.classList.remove('is-disabled');
          this.nextBtn.classList.remove('is-disabled');
          this.prevBtn.disabled = false;
          this.nextBtn.disabled = false;
          return;
        }

        const atStart = swiper.isBeginning;
        const atEnd = swiper.isEnd;
        this.prevBtn.classList.toggle('is-disabled', atStart);
        this.nextBtn.classList.toggle('is-disabled', atEnd);
        this.prevBtn.disabled = atStart;
        this.nextBtn.disabled = atEnd;
      }

      destroySwiper() {
        if (!this._activeSwiper) return;
        this._activeSwiper.destroy(true, true);
        this._activeSwiper = null;
      }
    }
  );
}
